import { BellSlash } from '@phosphor-icons/react';
import { useUnsubscribe } from '@reelplicate/api-client/notifications';
import { Button, Skeleton, Stack, Well } from '@reelplicate/ui';
import { useEffect, useRef } from 'react';
import { Link as RouterLink, useLocation } from 'react-router';

import { AuthBody, AuthCard, AuthHeader } from '@/features/auth/auth-card';

function SettingsButton() {
  return (
    <Button asChild variant="outline" size="lg" className="w-full">
      <RouterLink to="/settings/notifications">Notification settings</RouterLink>
    </Button>
  );
}

/**
 * Where an email's unsubscribe link lands (`/unsubscribe?token=…`), signed
 * in or out: the token names the person, workspace and email, so it turns
 * that email off straight away and says what it did.
 */
export function UnsubscribePage() {
  const location = useLocation();
  const token = new URLSearchParams(location.search).get('token') ?? '';
  const unsubscribe = useUnsubscribe({ mutation: { meta: { silent: true } } });
  const { mutate } = unsubscribe;
  const sent = useRef(false);

  useEffect(() => {
    // Once, even when React runs effects twice in development.
    if (!token || sent.current) return;
    sent.current = true;
    mutate({ data: { token } });
  }, [token, mutate]);

  if (!token || unsubscribe.isError) {
    return (
      <AuthCard>
        <Well tone="danger" title="This unsubscribe link isn’t valid">
          It may have been cut short when it was copied. You can turn emails off in your
          notification settings instead.
        </Well>
        <SettingsButton />
      </AuthCard>
    );
  }
  if (!unsubscribe.data) {
    return (
      <AuthCard>
        <Stack gap={4} align="center" aria-busy="true" aria-label="Unsubscribing">
          <Skeleton shape="rectangle" shimmer className="size-14 rounded-xl" />
          <Skeleton className="h-7 w-48" />
          <Skeleton className="w-64" />
        </Stack>
      </AuthCard>
    );
  }
  const { label, workspaceName } = unsubscribe.data;
  return (
    <AuthCard>
      <AuthHeader
        icon={{ icon: BellSlash, tone: 'brand' }}
        title="You’re unsubscribed"
        subtitle={`You won’t get the ${label.toLowerCase()} for ${workspaceName} any more.`}
      />
      <AuthBody>
        <p className="text-body-md text-text-secondary text-center">
          Changed your mind? Turn it back on in your notification settings.
        </p>
        <SettingsButton />
      </AuthBody>
    </AuthCard>
  );
}
